import clsx from "clsx";
import {
  BriefcaseFill,
  ChevronDown,
  ChevronUp,
  ClipboardPlus,
  PencilFill,
  PeopleFill,
  PersonFill,
} from "react-bootstrap-icons";
import { useHistory } from "react-router-dom";
import { type MiscContent, Role } from "@/briefpoint-client";
import { getPartyDisplay } from "@/components/DocumentWizard/SpecialInterrogatoriesStep/Selections/utils";
import { TagsList } from "@/components/TagsList";
import { useAuth } from "@/hooks/useAuth";
import ReactLogo from "@/images/logo.svg?react";
import { caseTypeDisplay } from "@/lib/utils";
import type { GroupedContentWithExpanded } from "./QuestionsTable";
import styles from "./QuestionTableRow.module.scss";

interface Props {
  questionKey: string;
  availableTags: Map<number, string>;
  groupedQuestions: GroupedContentWithExpanded;
  setExpanded: (id: string, expanded: boolean) => void;
}

export default function QuestionTableRow(props: Props) {
  const group = props.groupedQuestions[props.questionKey]?.questions;
  const question = group?.[0];
  const expanded = props.groupedQuestions[props.questionKey]?.expanded;
  const { jurisdictions } = useAuth();
  const { user } = useAuth();
  const ca_superior_court = (jurisdictions ?? [])[0];
  const history = useHistory();

  function editQuestion(content: MiscContent[], copy = false) {
    history.push({
      pathname: "/library/addeditquestion",
      search: `${copy ? "?copy=true" : ""}`,
      state: content,
    });
  }

  return (
    <>
      <tr className={styles.tableRowMain} key={question?.id}>
        <td className={styles.icon}>
          {question?.sharedWithFirmId ? (
            <BriefcaseFill title="Firm Standard" />
          ) : !question?.ownerId ? (
            <ReactLogo title="Briefpoint Default" />
          ) : null}
        </td>
        <td className={clsx(styles.tableCell, styles.caseType)}>
          {caseTypeDisplay(question?.caseTypeId, ca_superior_court, true)}
        </td>
        <td className={clsx(styles.tableCell, styles.tags)}>
          <TagsList
            tagIds={question?.tagIds}
            availableTags={props.availableTags}
            questionId={question?.id ?? ""}
            startTrimmingTagsAfter={3}
          />
        </td>
        <td className={clsx(styles.tableCell, styles.respondentType)}>
          {question?.respondentType === undefined
            ? ""
            : getPartyDisplay(question?.respondentType, false)}
        </td>
        <td className={clsx(styles.tableCell, styles.representative)}>
          {question?.isForRepresentative === undefined ? (
            ""
          ) : question?.isForRepresentative === true ? (
            <PeopleFill title="Representative" />
          ) : (
            <PersonFill title="Individual" />
          )}
        </td>
        <td className={clsx(styles.tableCell, styles.numberOfQuestions)}>
          {group.length}
        </td>
        <td className={clsx(styles.tableCell, styles.actions)}>
          {question?.ownerId &&
            (user?.role?.includes(Role.FirmAdmin) ||
              !question?.sharedWithFirmId) && (
              <PencilFill
                onClick={() => editQuestion(group ?? [])}
                className={styles.tableEditItem}
                title="Edit"
              />
            )}
          <ClipboardPlus
            onClick={() => editQuestion(group ?? [], true)}
            className={styles.tableEditItem}
            title="Copy"
          />
          {expanded ? (
            <ChevronUp
              title="Hide Questions"
              onClick={() => props.setExpanded(props.questionKey, false)}
            />
          ) : (
            <ChevronDown
              title="Show Questions"
              onClick={() => props.setExpanded(props.questionKey, true)}
            />
          )}
        </td>
      </tr>

      <tr className={styles.tableRow} key={`${question?.id}-content`}>
        {expanded && (
          <>
            <td style={{ width: "15px" }}>&nbsp;</td>
            <td className={clsx(styles.tableCell, "p-0")} colSpan={6}>
              <ol>
                {group.map((q) => (
                  <li key={q.id}>
                    <span>{q.content}</span>
                  </li>
                ))}
              </ol>
            </td>
          </>
        )}
      </tr>
    </>
  );
}
